import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import {
    EnterpriseDto,
    EnterpriseMembershipDto,
    EnterpriseMembershipStatus,
    GroupSearchService
} from 'lib';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    of,
    Subject,
    switchMap
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
    NgSelectComponent,
    NgHeaderTemplateDirective,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective
} from '@ng-select/ng-select';
import { EnterpriseGroupService } from '../../services/enterprise-group.service';
import { OrganizationSiteDto } from '../../interfaces/organization-site-dto';

@Component({
    selector: 'mmoga-add-to-organization',
    imports: [
        Dialog,
        NgSelectComponent,
        AsyncPipe,
        FormsModule,
        NgOptionTemplateDirective,
        NgHeaderTemplateDirective,
        NgLabelTemplateDirective
    ],
    templateUrl: './add-to-organization.component.html',
    standalone: true,
    styleUrl: './add-to-organization.component.scss'
})
export class AddToOrganizationComponent implements OnInit {
    private readonly groupSearchService = inject(GroupSearchService);
    private readonly toastrService = inject(ToastrService);
    private readonly enterpriseGroupService = inject(EnterpriseGroupService);

    @Input() showModal = false;
    @Input() organizationSiteDto: OrganizationSiteDto | undefined;
    @Output() organizationAdded = new EventEmitter<boolean>();

    enterprises = new Observable<EnterpriseDto[]>;
    enterpriseSearchSubject = new Subject<string>;

    selectedEnterprise: EnterpriseDto | undefined = undefined;

    ngOnInit(): void {
        this.initializeEnterpriseSearch();
    }

    initializeEnterpriseSearch(): void {
        this.enterprises = this.enterpriseSearchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                filter(searchTerm => searchTerm?.length === 0 || searchTerm?.length > 2),
                switchMap(searchTerm => this.groupSearchService.getGroups(searchTerm, 0, 5, 'name', 'ASC')
                    .pipe(
                        map(response => response.content),
                        catchError(() => {
                            this.toastrService.error('Failed to retrieve organizations.');
                            return of([]);
                        })
                    ))
            );
    }

    addToOrganization(): void {
        this.enterpriseGroupService.getEnterprise(this.selectedEnterprise!.id as number).subscribe({
            next: (enterprise) => {
                const memberships = enterprise.enterpriseMemberships ?? [];
                memberships.push({
                    organization: {
                        id: this.organizationSiteDto!.id
                    },
                    status: EnterpriseMembershipStatus.PENDING
                } as EnterpriseMembershipDto);

                const updateDto: Partial<EnterpriseDto> = {
                    id: enterprise.id,
                    name: enterprise.name,
                    type: enterprise.type,
                    alias: enterprise.alias,
                    disabled: enterprise.disabled,
                    securityDomain: enterprise.securityDomain,
                    enterpriseMemberships: memberships.map(m => ({
                        organization: { id: m.organization?.id },
                        status: m.status,
                        confirmedBy: m.confirmedBy,
                        confirmedDatetime: m.confirmedDatetime
                    })) as EnterpriseMembershipDto[]
                };

                this.enterpriseGroupService.createEnterprise(updateDto).subscribe({
                    next: () => {
                        this.toastrService.success(
                            `Successfully added ${this.organizationSiteDto!.organizationName} to ${this.selectedEnterprise!.name}.`
                        );
                        this.organizationAdded.emit(true);
                    },
                    error: () => {
                        this.toastrService.error('Failed to add site to organization.');
                    }
                });
            },
            error: () => {
                this.toastrService.error('Failed to retrieve organization details.');
            }
        });
    }

    closeModal(): void {
        this.selectedEnterprise = undefined;
        this.organizationAdded.emit(false);
    }
}
